import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';

import { breakpoints } from '../../constants';
import { Flex } from '../grid';
import { Icon } from '../icons';
import { PointsEditSection } from '../points-edit-section';
import { PointsRedemptionToggleRow } from '../points-redemption-toggle-row';

const PopoverBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  z-index: 1300;

  @media (min-width: ${breakpoints.md}) {
    left: auto;
    right: 0;
    width: 520px;
  }
`;

const PopoverPanel = styled.div`
  background-color: ${({ theme }) => theme.colors.warmWhite2};
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  padding: 20px 16px;
  position: relative;
`;

const PopoverCloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 100px;
  border: 0;
  background-color: ${({ theme }) => theme.colors.white};
  cursor: pointer;
`;

const CartPointsRedemption = ({
  availablePointsDollars,
  pointsToggleChecked,
  onPointsToggleChange,
  pointsAmountDollars,
  maxPointsAmountDollars,
  onPointsAmountChange,
  readOnly,
}) => {
  const [isPointsEditorOpen, setIsPointsEditorOpen] = useState(false);
  const [portalNode, setPortalNode] = useState(null);
  const editableMaxDollar = maxPointsAmountDollars ?? parseFloat(availablePointsDollars ?? '0') ?? 0;
  const editableAmount = pointsAmountDollars ?? editableMaxDollar;

  useEffect(() => {
    if (typeof document !== 'undefined') {
      setPortalNode(document.body);
    }
  }, []);

  const handlePointsToggle = checked => {
    onPointsToggleChange(checked);
    setIsPointsEditorOpen(checked);
  };

  const closeEditor = () => setIsPointsEditorOpen(false);

  const showEditor = !readOnly && isPointsEditorOpen && portalNode;

  return (
    <>
      <Flex
        alignItems="center"
        gap="12px"
        px="14px"
        py="10px"
        backgroundColor="warmWhite1"
        style={{ borderRadius: '13px' }}
      >
        <PointsRedemptionToggleRow
          availablePointsDollars={availablePointsDollars}
          pointsToggleChecked={pointsToggleChecked}
          onPointsToggleChange={handlePointsToggle}
          pointsAmountDollars={pointsAmountDollars}
          readOnly={readOnly}
        />
      </Flex>

      {showEditor &&
        createPortal(
          <PopoverBackdrop onClick={closeEditor}>
            <PopoverPanel onClick={event => event.stopPropagation()}>
              <PopoverCloseButton type="button" aria-label="Close" onClick={closeEditor}>
                <Icon name="cross" height="24px" width="24px" />
              </PopoverCloseButton>
              <Flex flexDirection="column" alignItems="center" gap="12px">
                <Icon name="periPerksLogo" height="48px" width="64px" stroke="none" />
                <PointsEditSection
                  amount={editableAmount}
                  maxAmount={editableMaxDollar}
                  onChange={onPointsAmountChange}
                  onConfirm={closeEditor}
                />
              </Flex>
            </PopoverPanel>
          </PopoverBackdrop>,
          portalNode
        )}
    </>
  );
};

CartPointsRedemption.propTypes = {
  availablePointsDollars: PropTypes.string,
  pointsToggleChecked: PropTypes.bool,
  onPointsToggleChange: PropTypes.func,
  pointsAmountDollars: PropTypes.number,
  maxPointsAmountDollars: PropTypes.number,
  onPointsAmountChange: PropTypes.func,
  readOnly: PropTypes.bool,
};

CartPointsRedemption.defaultProps = {
  availablePointsDollars: null,
  pointsToggleChecked: false,
  onPointsToggleChange: () => {},
  pointsAmountDollars: null,
  maxPointsAmountDollars: null,
  onPointsAmountChange: () => {},
  readOnly: false,
};

export { CartPointsRedemption };
